import { supabase } from '../lib/supabase';

// Module-level variables for broadcast subscription management
let channel = null;
let currentUserId = null;
let authListener = null;

export const inAppNotificationService = {
  /**
   * Get user notification preferences
   */
  async getUserPreferences() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_notification_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      // Return default preferences if none exist
      if (!data) {
        return {
          notifyOnSent: true,
          notifyOnDelivered: true,
          notifyOnBounced: true,
          notifyOnFailed: true,
          notifyOnOpened: false,
          notifyOnClicked: false,
          enableToastNotifications: true,
          enableNotificationCenter: true,
          quietHoursEnabled: false,
          quietHoursStart: null,
          quietHoursEnd: null
        };
      }

      return {
        id: data?.id,
        userId: data?.user_id,
        notifyOnSent: data?.notify_on_sent,
        notifyOnDelivered: data?.notify_on_delivered,
        notifyOnBounced: data?.notify_on_bounced,
        notifyOnFailed: data?.notify_on_failed,
        notifyOnOpened: data?.notify_on_opened,
        notifyOnClicked: data?.notify_on_clicked,
        enableToastNotifications: data?.enable_toast_notifications,
        enableNotificationCenter: data?.enable_notification_center,
        quietHoursEnabled: data?.quiet_hours_enabled,
        quietHoursStart: data?.quiet_hours_start,
        quietHoursEnd: data?.quiet_hours_end,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  },

  /**
   * Update user notification preferences
   */
  async updateUserPreferences(preferences) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {
        user_id: user?.id,
        notify_on_sent: preferences?.notifyOnSent,
        notify_on_delivered: preferences?.notifyOnDelivered,
        notify_on_bounced: preferences?.notifyOnBounced,
        notify_on_failed: preferences?.notifyOnFailed,
        notify_on_opened: preferences?.notifyOnOpened,
        notify_on_clicked: preferences?.notifyOnClicked,
        enable_toast_notifications: preferences?.enableToastNotifications,
        enable_notification_center: preferences?.enableNotificationCenter,
        quiet_hours_enabled: preferences?.quietHoursEnabled,
        quiet_hours_start: preferences?.quietHoursStart,
        quiet_hours_end: preferences?.quietHoursEnd
      };

      const { data, error } = await supabase
        ?.from('user_notification_preferences')
        ?.upsert(updateData, { onConflict: 'user_id' })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        notifyOnSent: data?.notify_on_sent,
        notifyOnDelivered: data?.notify_on_delivered,
        notifyOnBounced: data?.notify_on_bounced,
        notifyOnFailed: data?.notify_on_failed,
        notifyOnOpened: data?.notify_on_opened,
        notifyOnClicked: data?.notify_on_clicked,
        enableToastNotifications: data?.enable_toast_notifications,
        enableNotificationCenter: data?.enable_notification_center,
        quietHoursEnabled: data?.quiet_hours_enabled,
        quietHoursStart: data?.quiet_hours_start,
        quietHoursEnd: data?.quiet_hours_end,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  },

  /**
   * Get all notifications for current user
   */
  async getNotifications(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('in_app_notifications')
        ?.select(`
          *,
          email_delivery_logs (
            id,
            booking_id,
            recipient_email,
            status,
            resend_email_id
          )
        `)
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.isRead !== undefined) {
        query = query?.eq('is_read', filters?.isRead);
      }

      if (filters?.notificationType && filters?.notificationType !== 'all') {
        query = query?.eq('notification_type', filters?.notificationType);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        userId: notification?.user_id,
        emailLogId: notification?.email_log_id,
        title: notification?.title,
        message: notification?.message,
        notificationType: notification?.notification_type,
        isRead: notification?.is_read,
        readAt: notification?.read_at,
        metadata: notification?.metadata,
        createdAt: notification?.created_at,
        updatedAt: notification?.updated_at,
        emailLog: notification?.email_delivery_logs ? {
          id: notification?.email_delivery_logs?.id,
          bookingId: notification?.email_delivery_logs?.booking_id,
          recipientEmail: notification?.email_delivery_logs?.recipient_email,
          status: notification?.email_delivery_logs?.status,
          resendEmailId: notification?.email_delivery_logs?.resend_email_id
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    try {
      const { data, error } = await supabase?.rpc('get_unread_notification_count');

      if (error) throw error;

      return data || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase?.rpc('mark_notification_read', {
        notification_id: notificationId
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      const { error } = await supabase?.rpc('mark_all_notifications_read');

      if (error) throw error;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time notification updates
   */
  async subscribeToNotifications(onInsert, onUpdate) {
    try {
      // Properly await the async getUser() call
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        console.warn('No authenticated user for notification subscription');
        return null;
      }

      let channel = supabase?.channel('in_app_notifications_changes')?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'in_app_notifications',
            filter: `user_id=eq.${user?.id}`
          },
          (payload) => {
            if (onInsert) {
              onInsert(payload?.new);
            }
          }
        )?.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'in_app_notifications',
            filter: `user_id=eq.${user?.id}`
          },
          (payload) => {
            if (onUpdate) {
              onUpdate(payload?.new, payload?.old);
            }
          }
        )?.subscribe();

      return channel;
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      throw error;
    }
  },

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('in_app_notifications')
        ?.delete()
        ?.eq('id', notificationId)
        ?.eq('user_id', user?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  /**
   * Delete all read notifications
   */
  async deleteAllRead() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('in_app_notifications')
        ?.delete()
        ?.eq('user_id', user?.id)
        ?.eq('is_read', true);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      throw error;
    }
  }
};

function ensureSubscribed(userId, onInsert, onUpdate) {
  // Idempotent: prevent duplicates and handle user switch
  if (currentUserId === userId && channel?.state === 'subscribed') return;

  if (channel) {
    supabase?.removeChannel(channel);
    channel = null;
  }

  currentUserId = userId;

  // Private, per-user topic
  channel = supabase?.channel(`user:${userId}:in_app_notifications`, {
    config: { private: true, broadcast: { self: true, ack: true } }
  });

  channel?.on('broadcast', { event: 'notification_inserted' }, (payload) => {
      // Trigger sends { record: NEW }
      onInsert?.(payload?.record ?? payload?.payload ?? payload);
    })?.on('broadcast', { event: 'notification_updated' }, (payload) => {
      // Trigger sends { new, old }
      const n = payload?.new ?? payload?.payload?.new ?? payload?.record;
      const o = payload?.old ?? payload?.payload?.old;
      onUpdate?.(n, o);
    })?.subscribe();
}

export function cleanupNotifications() {
  if (channel) {
    supabase?.removeChannel(channel);
    channel = null;
  }
  currentUserId = null;
  if (authListener) {
    authListener?.subscription?.unsubscribe();
    authListener = null;
  }
}
function subscribeToNotifications(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: subscribeToNotifications is not implemented yet.', args);
  return null;
}

export { subscribeToNotifications };